import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Card,
  CardTitle,
  ChevronLeftIcon,
  Input,
  LoadingBlock,
  Select,
  Textarea,
  TrashIcon,
  cn,
} from '@quoter/web-core'
import { useI18n } from '@/hooks/useI18n'
import { useAddEntry, useDiaryDay, useRemoveEntry, useSaveSymptom, useSymptoms } from '@/hooks/useDiary'
import { useJourney } from '@/hooks/useJourney'
import { useChallenges } from '@/hooks/useChallenges'
import { MEALS, type Meal } from '@/lib/db-types'
import { addDays, daysBetween, fromIsoDate, today, weekEnding } from '@/lib/dates'
import { CHALLENGE_KEYS, progress, type Score } from '@/lib/plan'
import { SCORE_DOT, ScorePicker, scoreLabel } from '@/components/ScorePicker'

/** The diary: one day at a time — what was eaten, and how the gut felt. */
export function Today() {
  const { t, formatLongDate } = useI18n()
  const [date, setDate] = useState(today)
  const isToday = date === today()

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-[34px] font-normal tracking-tightish text-gray-900">
            {isToday ? t.today.title : formatLongDate(fromIsoDate(date))}
          </h1>
          {isToday && <p className="mt-0.5 text-[13.5px] text-gray-500">{formatLongDate(fromIsoDate(date))}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <DayButton label={t.today.previousDay} onClick={() => setDate(addDays(date, -1))}>
            <ChevronLeftIcon size={18} />
          </DayButton>
          <DayButton label={t.today.nextDay} onClick={() => setDate(addDays(date, 1))} disabled={isToday}>
            <span className="rotate-180">
              <ChevronLeftIcon size={18} />
            </span>
          </DayButton>
        </div>
      </header>

      {!isToday && (
        <button
          type="button"
          onClick={() => setDate(today())}
          className="text-[13px] font-semibold text-brand-700 hover:underline"
        >
          {t.today.backToToday}
        </button>
      )}

      {isToday && <PhaseCard />}
      <CheckIn date={date} />
      <Meals date={date} />
      <Week end={today()} selected={date} onSelect={setDate} />
    </div>
  )
}

function DayButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-DEFAULT text-gray-700 transition-colors hover:bg-gray-900/[0.07] disabled:opacity-35"
    >
      {children}
    </button>
  )
}

/** Where the person is in the plan, with the way into it. */
function PhaseCard() {
  const { t, fmt } = useI18n()
  const { journey } = useJourney()
  const { challenges } = useChallenges()

  const active = CHALLENGE_KEYS.find((k) => challenges[k]?.status === 'in_progress')
  const { done, total } = progress(Object.values(challenges).map((c) => c.status))

  const line =
    journey.phase === 'elimination'
      ? journey.eliminationStartedOn
        ? fmt(t.today.phaseCard.elimination, { n: daysBetween(journey.eliminationStartedOn, today()) + 1 })
        : t.today.phaseCard.eliminationNoDate
      : journey.phase === 'reintroduction'
        ? fmt(t.today.phaseCard.reintroduction, { done, total })
        : t.today.phaseCard.personalization

  return (
    <Link
      to="/plan"
      className="flex items-center justify-between gap-4 rounded-lg bg-sage-100 px-5 py-4 text-sage-900 transition-colors hover:bg-sage-200"
    >
      <span className="min-w-0">
        <span className="block font-display text-[18px]">{line}</span>
        {active && (
          <span className="mt-0.5 block text-[13px] text-sage-800">
            {fmt(t.today.phaseCard.activeChallenge, { name: t.plan.challenges[active].name })}
          </span>
        )}
      </span>
      <span className="shrink-0 text-[13px] font-semibold underline underline-offset-2">{t.today.phaseCard.openPlan}</span>
    </Link>
  )
}

/** The daily check-in: one tap for the score, an optional note. */
function CheckIn({ date }: { date: string }) {
  const { t } = useI18n()
  // The same range as the week strip whenever the day is inside it, so the two
  // share one cached query; an older day widens the range to reach it.
  const weekStart = addDays(today(), -6)
  const { byDate } = useSymptoms(date < weekStart ? date : weekStart, today())
  const save = useSaveSymptom()
  const log = byDate[date]
  const [note, setNote] = useState('')
  const [savedFlash, setSavedFlash] = useState(false)

  // Follow the stored note when the day (or its row) changes.
  useEffect(() => {
    setNote(log?.note ?? '')
  }, [date, log?.note])
  // The "Saved" tick belongs to one day; it must survive the refetch that
  // follows a save, so it is cleared on a day change only.
  useEffect(() => {
    setSavedFlash(false)
  }, [date])

  function pick(score: Score) {
    save.mutate({ date, score, note })
  }

  // A note rides on the day's check-in, so there must be a score first —
  // saving a note alone would otherwise record a "fine" nobody chose.
  function saveNote() {
    if (!log) return
    save.mutate({ date, score: log.score, note: note.trim() }, { onSuccess: () => setSavedFlash(true) })
  }

  return (
    <Card>
      <CardTitle>{t.scores.label}</CardTitle>
      <div className="mt-4">
        <ScorePicker value={(log?.score as Score | undefined) ?? null} onChange={pick} disabled={save.isPending} />
      </div>
      <div className="mt-5">
        <label htmlFor="day-note" className="mb-1.5 block text-[12.5px] font-semibold text-gray-600">
          {t.today.noteLabel}
        </label>
        <Textarea
          id="day-note"
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => {
            setNote(e.target.value)
            setSavedFlash(false)
          }}
          placeholder={t.today.notePlaceholder}
        />
        <div className="mt-3 flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={saveNote}
            disabled={!log || save.isPending || note.trim() === log.note}
          >
            {t.today.saveNote}
          </Button>
          {savedFlash && <span className="text-[12.5px] text-green-700">{t.today.saved}</span>}
          {save.isError && (
            <span className="text-[12.5px] text-red-700" role="alert">
              {t.common.error}
            </span>
          )}
        </div>
      </div>
    </Card>
  )
}

/** What was eaten that day, and the form to add to it. */
function Meals({ date }: { date: string }) {
  const { t } = useI18n()
  const { entries, loading, error } = useDiaryDay(date)
  const add = useAddEntry(date)
  const remove = useRemoveEntry(date)
  const [meal, setMeal] = useState<Meal>('breakfast')
  const [body, setBody] = useState('')

  function submit(e: FormEvent) {
    e.preventDefault()
    const value = body.trim()
    if (!value || add.isPending) return
    add.mutate({ meal, body: value }, { onSuccess: () => setBody('') })
  }

  return (
    <Card>
      <CardTitle>{t.today.whatDidYouEat}</CardTitle>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-2.5 sm:flex-row">
        <Select
          value={meal}
          onChange={(e) => setMeal(e.target.value as Meal)}
          aria-label={t.today.whatDidYouEat}
          className="sm:w-44"
        >
          {MEALS.map((m) => (
            <option key={m} value={m}>
              {t.today.meals[m]}
            </option>
          ))}
        </Select>
        <Input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t.today.placeholder}
          aria-label={t.today.placeholder}
          maxLength={500}
          className="flex-1"
        />
        <Button type="submit" disabled={add.isPending || !body.trim()}>
          {add.isPending ? t.common.saving : t.today.add}
        </Button>
      </form>
      {(add.isError || remove.isError) && (
        <p className="mt-2 text-[12.5px] text-red-700" role="alert">
          {t.common.error}
        </p>
      )}

      <div className="mt-5">
        {loading ? (
          <LoadingBlock />
        ) : error ? (
          <p className="text-[13px] text-red-700" role="alert">
            {t.common.error}
          </p>
        ) : entries.length === 0 ? (
          <p className="rounded-md bg-surface-muted px-4 py-5 text-center text-[13.5px] text-gray-500">
            {t.today.emptyDay}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-start gap-3 py-2.5">
                <span className="w-28 shrink-0 pt-0.5 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                  {t.today.meals[entry.meal]}
                </span>
                <span className="min-w-0 flex-1 break-words text-[14px] text-gray-900">{entry.body}</span>
                <button
                  type="button"
                  aria-label={t.today.remove}
                  title={t.today.remove}
                  onClick={() => remove.mutate(entry.id)}
                  disabled={remove.isPending}
                  className="shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-900/[0.07] hover:text-red-600"
                >
                  <TrashIcon size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  )
}

/** Seven dots: the last week's check-ins. Tap one to open that day. */
function Week({ end, selected, onSelect }: { end: string; selected: string; onSelect: (date: string) => void }) {
  const { t, intlLocale } = useI18n()
  const days = weekEnding(end)
  const { byDate } = useSymptoms(days[0]!, end)
  const any = days.some((d) => byDate[d])

  return (
    <Card tone="muted">
      <CardTitle>{t.today.weekTitle}</CardTitle>
      <div className="mt-4 grid grid-cols-7 gap-1.5">
        {days.map((day) => {
          const log = byDate[day]
          const weekday = fromIsoDate(day).toLocaleDateString(intlLocale, { weekday: 'short' })
          const label = log ? scoreLabel(t, log.score as Score) : t.today.notLogged
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelect(day)}
              aria-label={`${weekday} ${fromIsoDate(day).getDate()}: ${label}`}
              aria-pressed={day === selected}
              className={cn(
                'flex flex-col items-center gap-1.5 rounded-md py-2 transition-colors hover:bg-gray-900/[0.06]',
                day === selected && 'bg-surface shadow-card',
              )}
            >
              <span className="text-[11px] font-semibold uppercase text-gray-500">{weekday}</span>
              <span
                className={cn(
                  'h-6 w-6 rounded-full',
                  log ? SCORE_DOT[log.score as Score] : 'border border-dashed border-strong',
                )}
              />
              <span className="text-[12px] text-gray-600">{fromIsoDate(day).getDate()}</span>
            </button>
          )
        })}
      </div>
      {!any && <p className="mt-3 text-[12.5px] text-gray-500">{t.today.weekEmpty}</p>}
    </Card>
  )
}
